import { useState } from "react";
import { useLocation } from "wouter";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { IconBadge } from "@/components/waggle/PageShell";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { CANCEL_DETAILS_MAX, CANCEL_REASONS, cancelAccount, emailMatches, type CancelReason } from "@/lib/accountCancellation";
import { usePlan } from "@/lib/plan";

type Step = "consequences" | "reason" | "confirm";

// Three-step cancel flow in one dialog. The submit is stubbed (lib/accountCancellation.ts).
export function CancelAccountDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { user, logout } = useAuth();
  const { isUnlimited } = usePlan();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [step, setStep] = useState<Step>("consequences");
  const [reason, setReason] = useState<CancelReason | undefined>();
  const [details, setDetails] = useState("");
  const [typedEmail, setTypedEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setStep("consequences");
    setReason(undefined);
    setDetails("");
    setTypedEmail("");
    setError(null);
  };

  const handleOpenChange = (next: boolean) => {
    if (submitting) return;
    if (!next) reset();
    onOpenChange(next);
  };

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await cancelAccount({ reason, details: reason === "other" ? details.trim() || undefined : undefined });
    } catch (e) {
      setError(e instanceof Error ? e.message : "We couldn't cancel your account. Please try again.");
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
    reset();
    onOpenChange(false);
    await logout();
    setLocation("/");
    toast({ title: "Your account has been cancelled", description: "Your data has been deleted." });
  };

  const email = user?.email ?? "";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="cancel-account-dialog">
        <div>
          <IconBadge tone="muted">
            <AlertTriangle />
          </IconBadge>
          <DialogTitle className="mt-5 pr-8 text-lg">
            {step === "consequences" && "Cancel your account?"}
            {step === "reason" && "Why are you leaving?"}
            {step === "confirm" && "Confirm cancellation"}
          </DialogTitle>
          <DialogDescription className="mt-1">
            {step === "consequences" && "This permanently deletes your account and cannot be undone."}
            {step === "reason" && "Optional. It helps us improve Waggle."}
            {step === "confirm" && `Type ${email} to confirm.`}
          </DialogDescription>
        </div>

        {step === "consequences" && (
          <div className="flex flex-col gap-3 text-sm text-muted-foreground">
            <ul className="list-disc space-y-1.5 pl-5">
              <li>Your tests, blueprints and uploaded source material are deleted.</li>
              <li>You will no longer be able to sign in.</li>
              <li>Download anything you want to keep first. There is no way to recover it afterwards.</li>
            </ul>
            {isUnlimited && (
              <p className="rounded-md border bg-muted px-3 py-2" data-testid="cancel-account-pro-note">
                Your Pro subscription will be cancelled and you won't be charged again.
              </p>
            )}
          </div>
        )}

        {step === "reason" && (
          <div className="flex flex-col gap-3">
            <RadioGroup value={reason ?? ""} onValueChange={(v) => setReason(v as CancelReason)}>
              {CANCEL_REASONS.map((r) => (
                <div key={r.id} className="flex items-center gap-2">
                  <RadioGroupItem value={r.id} id={`cancel-reason-${r.id}`} data-testid={`cancel-account-reason-${r.id}`} />
                  <Label htmlFor={`cancel-reason-${r.id}`} className="font-normal">
                    {r.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
            {reason === "other" && (
              <Textarea
                value={details}
                onChange={(e) => setDetails(e.target.value.slice(0, CANCEL_DETAILS_MAX))}
                placeholder="Tell us more (optional)"
                aria-label="More detail"
                data-testid="cancel-account-details"
              />
            )}
          </div>
        )}

        {step === "confirm" && (
          <div className="flex flex-col gap-2">
            <Input
              value={typedEmail}
              onChange={(e) => setTypedEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
              placeholder={email}
              aria-label="Account email"
              autoComplete="off"
              disabled={submitting}
              data-testid="cancel-account-email-input"
            />
            {error && (
              <p role="alert" className="text-sm text-destructive" data-testid="cancel-account-error">
                {error}
              </p>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          {step === "consequences" && (
            <>
              <Button variant="outline" onClick={() => handleOpenChange(false)} data-testid="cancel-account-keep">
                Keep account
              </Button>
              <Button variant="destructive" onClick={() => setStep("reason")} data-testid="cancel-account-continue">
                Continue
              </Button>
            </>
          )}
          {step === "reason" && (
            <>
              <Button variant="outline" onClick={() => setStep("consequences")} data-testid="cancel-account-back">
                Back
              </Button>
              <Button variant="destructive" onClick={() => setStep("confirm")} data-testid="cancel-account-continue">
                Continue
              </Button>
            </>
          )}
          {step === "confirm" && (
            <>
              <Button variant="outline" onClick={() => setStep("reason")} disabled={submitting} data-testid="cancel-account-back">
                Back
              </Button>
              <Button
                variant="destructive"
                onClick={submit}
                disabled={submitting || !emailMatches(typedEmail, email)}
                data-testid="cancel-account-confirm"
              >
                {submitting ? "Deleting…" : "Permanently delete my account"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
